import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth/withWidth';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import CardActions from '@material-ui/core/CardActions';
import { grey } from '@material-ui/core/colors';
import Grid from '@material-ui/core/Grid';
import CustomDialog from '@configuratorware/configurator-frontendgui/Shared/Components/CustomDialog';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import AnalyticsWrapper from '@configuratorware/configurator-frontendgui/App/Shared/Components/Analytics/AnalyticsWrapper';
import customClassName from '@configuratorware/configurator-frontendgui/App/Utils/customClassName';
import MainButton from '@configuratorware/configurator-frontendgui/Shared/Components/MainButton';

import Dialog from '@material-ui/core/Dialog/Dialog';
import DialogContent from '@material-ui/core/DialogContent/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions/DialogActions';
import Icon from '@material-ui/core/Icon/Icon';
import RootRef from '@material-ui/core/RootRef/RootRef';
import { InView } from 'react-intersection-observer';
import PriceOverview from '@configuratorware/configurator-frontendgui/App/Shared/Components/PriceOverview';
import Notification from '@configuratorware/configurator-frontendgui/App/Shared/Components/Notification';
import PriceList from '@configuratorware/configurator-frontendgui/App/Shared/Components/PriceList';
import AddToCart from '@configuratorware/configurator-frontendgui/App/Shared/Components/AddToBasket';
import ReceiveOfferForm from '@configuratorware/configurator-frontendgui/App/Shared/Components/ReceiveOfferForm';
import AcceptDesign from '@configuratorware/configurator-frontendgui/App/Shared/Components/AcceptDesign';
import LoadingOverlay from '@configuratorware/configurator-frontendgui/Framework/Components/LoadingOverlay';
import { isEqual } from 'lodash';
import Progress from '@configuratorware/configurator-frontendgui/App/Shared/Components/Progress/Progress';

const styles = (theme) => ({
  root: {
    bottom: 0,
    right: 0,
    position: 'absolute',
    [theme.breakpoints.down('xs')]: {
      right: 0,
      left: 0,
    },
    [theme.breakpoints.between('xs', 'sm')]: {
      width: '100%',
    },
    [theme.breakpoints.between('sm', 'xl')]: {
      width: '320px',
    },
  },
  summary: {
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      marginRight: 5,
    },
  },
  summaryDialog: {
    '& .MuiDialog-container': {
      '& .MuiDialog-paperWidthMd': {
        maxWidth: 600,
      },
    },
    '& .MuiDialog-paperScrollPaper': {
      borderColor: 'transparent',
    },
  },
  summaryDialogContent: {
    padding: '0px 32px 24px',
    overflow: 'auto',
  },
  categoryTitle: {
    fontWeight: 'bold',
    fontSize: 20,
    marginTop: 16,
  },
  selectedOptionWithPrice: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  optionDescriptionText: {
    flex: 1,
  },
  contentPositioning: {
    paddingLeft: 32,
    paddingRight: 20,
    [theme.breakpoints.down('xs')]: {
      paddingLeft: 0,
      paddingRight: 8,
      marginRight: '-8px',
    },
    '& p': {
      fontSize: 15,
      letterSpacing: 'normal',
    },
    marginRight: 4,
    padding: 0,
  },
  dialogWrapper: {
    borderTop: '7px solid ' + theme.palette.primary.main,
    padding: '0 32px',
    overflow: 'hidden',
    [theme.breakpoints.down('xs')]: {
      padding: '0 16px',
    },
  },
  card: {
    marginBottom: 1,
    borderTop: '7px solid ' + theme.palette.primary.main,
    padding: 15,
    overflow: 'visible',
    [theme.breakpoints.between('xs', 'lg')]: {
      marginTop: 16,
    },
    [theme.breakpoints.down('xs')]: {
      overflow: 'hidden',
      display: 'flex',
      boxShadow: 'none',
      '&:last-child': {
        padding: '12px 0 0',
      },
      paddingTop: 0,
      borderTop: '1px solid ' + grey[500],
      borderRadius: 0,
    },
  },
  cardUnboxed: {
    borderTop: 'none',
    boxShadow: 'none',
    [theme.breakpoints.down('xs')]: {
      display: 'flex',
    },
  },
  cardContentWrapper: {
    [theme.breakpoints.down('xs')]: {
      '&:last-child': {
        paddingBottom: 4,
      },
    },
  },
  AmountPriceContent: {
    padding: 0,
    '&:last-child': {
      paddingBottom: 5,
    },
  },
  dialogPositioning: {
    overflow: 'hidden',
    '& .MuiDialog-container': {
      '& .MuiDialogTitle-root': {
        paddingTop: 25,
        paddingBottom: 15,
        '& p, h2': {
          fontSize: 24,
          fontWeight: 'bold',
          letterSpacing: 'normal',
        },
      },
      '& .MuiDialogActions-root': {
        paddingBottom: 24,
        [theme.breakpoints.down('xs')]: {
          marginRight: 0,
        },
        marginRight: 8,
      },
      '& .MuiDialogTitle-root, .MuiDialogActions-root': {
        paddingLeft: 32,
        paddingRight: 32,
        [theme.breakpoints.down('xs')]: {
          paddingLeft: 0,
          paddingRight: 0,
        },
        '& p': {
          fontSize: 15,
          letterSpacing: 'normal',
        },
      },
    },
    '& .MuiButton-root': {
      height: 40,
      textTransform: 'none',
      minWidth: 140,
      marginTop: 8,
      color: theme.palette.primary.darkText,
      [theme.breakpoints.down('xs')]: {
        height: 56,
      },
    },
  },
  AmountPriceButton: {
    textTransform: 'none',
    whiteSpace: 'nowrap',
    margin: 0,
    padding: '6px 15px',
    [theme.breakpoints.down('xs')]: {
      minWidth: 35,
      padding: 0,
    },
    color: theme.palette.primary.darkText,
  },
  summaryButton: {
    textTransform: 'none',
    whiteSpace: 'nowrap',
    marginBottom: 4,
    [theme.breakpoints.down('xs')]: {
      minWidth: 35,
    },
    borderBottom: '1px solid',
    borderRadius: 0,
    padding: 0,
    color: theme.palette.primary.darkText,
  },
  smallButtonSize: {
    [theme.breakpoints.down('xs')]: {
      fontSize: 12,
      lineHeight: 1,
    },
  },
  mainButton: {
    margin: 0,
    padding: '6px 15px',
    height: 40,
    lineHeight: 1.5,
    [theme.breakpoints.down('xs')]: {
      height: 56,
    },
  },
  mainButtonLabel: {
    [theme.breakpoints.down('xs')]: {
      whiteSpace: 'normal',
      fontSize: 12,
    },
  },
  AmountPriceTypography: {
    textAlign: 'end',
    lineHeight: 1.2,
    [theme.breakpoints.down('xs')]: {
      padding: '3px 0',
      textAlign: 'start',
      fontSize: 12,
      lineHeight: 1,
    },
  },
  AmountPriceTypographyVat: {
    fontSize: 9,
    lineHeight: 1.2,
    textAlign: 'end',
    display: 'block',

    [theme.breakpoints.down('xs')]: {
      fontSize: 9,
      textAlign: 'start',
    },
  },
  cardActions: {
    padding: '8px 0',
    [theme.breakpoints.down('xs')]: {
      padding: 0,
    },
  },
  closeIcon: {
    position: 'absolute',
    right: 32,
    top: 32,
    [theme.breakpoints.down('xs')]: {
      right: 16,
    },
    '&:hover': {
      cursor: 'pointer',
    },
  },
  dialogTitleRoot: {
    padding: '20px 0',
  },
});

const DialogActionsWrapper = ({ onTouchMove, ...props }) => {
  const ref = useRef();
  useEffect(() => {
    const { current } = ref;
    if (current) {
      current.addEventListener('touchmove', onTouchMove, { passive: false });
    }
    return () => {
      if (current) {
        current.removeEventListener('touchmove', onTouchMove, { passive: false });
      }
    };
  }, [onTouchMove]);
  return (
    // as of material-ui v3.x the refs are not forwarded, so the only way to get it is using RootRef
    // FIXME check after updating material-ui
    <RootRef rootRef={ref}>
      <DialogActions {...props} />
    </RootRef>
  );
};

DialogActionsWrapper.propTypes = {
  onTouchMove: PropTypes.func,
};

class AmountPrice extends React.Component {
  static propTypes = {
    boxed: PropTypes.bool,
    classes: PropTypes.object,
    amount: PropTypes.number,
    priceFormatted: PropTypes.string,
    vatNotice: PropTypes.string,
    ProductVariantComponent: PropTypes.elementType.isRequired,
    ConfigurationModeSwitchComponent: PropTypes.elementType.isRequired,
    showConfigurationModeSwitch: PropTypes.bool,
    error: PropTypes.object,
    width: PropTypes.string,
    hidePrices: PropTypes.bool,
    hidePieces: PropTypes.bool,
    priceList: PropTypes.array,
    showProductVariants: PropTypes.bool,
    title: PropTypes.string,
    showCallToAction: PropTypes.bool,
    showReceiveOfferForm: PropTypes.bool,
    onClose: PropTypes.func,
    sendReceiveOfferRequest: PropTypes.func,
    setConfigurationComment: PropTypes.func,
    onSelectableChanged: PropTypes.func,
    onAddToBasket: PropTypes.func,
    isBulkNamesSelected: PropTypes.bool,
    clientTexts: PropTypes.object,
    bulkSavings: PropTypes.array,
    notifications: PropTypes.array,
    displayAcceptDesign: PropTypes.bool,
    bulkNameErrors: PropTypes.array,
    onShowChange: PropTypes.func,
    wrapperClass: PropTypes.string,
    validateConfiguration: PropTypes.func,
    addToCartPopUpLoader: PropTypes.bool,
    summary: PropTypes.object,
    fetchSummary: PropTypes.func,
  };

  static defaultProps = {
    showCallToAction: true,
    showConfigurationModeSwitch: false,
    notifications: [],
    clientTexts: {
      termsAndConditionsLink: '',
    },
    hidePieces: false,
  };

  state = {
    showPriceOverview: false,
    notice: '',
    hasErrorReceiveOfferForm: false,
    bulkNameErrors: [],
    approval: {
      value: false,
      error: null,
    },
    inView: true,
    showErrors: false,
    showSummary: false,
  };
  componentDidUpdate(prevProps, prevState) {
    const { error, bulkNameErrors } = this.props;
    if (error && !isEqual(error, prevProps.error) && this.dialogContentRef.current) {
      this.dialogContentRef.current.scrollTo(0, 0);
    }
    if (bulkNameErrors !== prevProps.bulkNameErrors) {
      this.setState({ bulkNameErrors });
    }
    if (prevProps.addToCartPopUpLoader && !this.props.addToCartPopUpLoader) {
      this.handleClose();
    }
    const { showPriceOverview } = this.state;
    if (showPriceOverview !== prevState.showPriceOverview) {
      const { onShowChange } = this.props;
      onShowChange && onShowChange(showPriceOverview);
    }
  }

  receiveOfferFormComponentRef = React.createRef();
  dialogContentRef = React.createRef();
  priceListRef = React.createRef();

  handleChange = (event) => {
    this.setState({
      [event.target.name]: {
        value: event.target.checked,
        error: null,
      },
    });
  };

  verifyRequestErrors = () => {
    const { bulkNameErrors, displayAcceptDesign } = this.props;
    const designApproved = this.state.approval.value;
    if (bulkNameErrors && bulkNameErrors.length > 0) {
      this.setState({ showErrors: true });
      this.setState({ bulkNameErrors });
      this.dialogContentRef.current.scrollTo(0, 0);
    }
    if (displayAcceptDesign && !designApproved) {
      this.setState({
        approval: {
          value: false,
          error: true,
        },
      });
    }
  };

  requestHasErrors = () => {
    const { bulkNameErrors, displayAcceptDesign } = this.props;
    const designApproved = this.state.approval.value;
    if ((bulkNameErrors && bulkNameErrors.length > 0) || (displayAcceptDesign && !designApproved)) {
      return true;
    }
    return false;
  };

  onAddToBasket = () => {
    const { onAddToBasket } = this.props;
    if (!this.requestHasErrors()) {
      onAddToBasket();
    } else {
      this.verifyRequestErrors();
    }
  };

  handleTouchMove = ((evt) => {
    const dialogContent = this.dialogContentRef.current;
    evt.preventDefault();
    dialogContent.scrollTo(0, dialogContent.scrollTop);
  }).bind(this);

  openPriceOverview = async () => {
    try {
      await this.props.validateConfiguration();
      this.setState({
        showPriceOverview: !this.state.showPriceOverview,
        hasErrorReceiveOfferForm: false,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`Error while validating configuration: ${e}`);
    }
  };

  handleClose = () => {
    this.setState({
      showPriceOverview: false,
      bulkNameErrors: [],
      approval: {
        value: false,
        error: null,
      },
      showErrors: false,
    });
    this.props.onClose();
  };

  onReceiveOfferFormPress = async () => {
    const result = await this.props.sendReceiveOfferRequest(
      this.receiveOfferFormComponentRef.current.validate()
    );
    const designApproved = this.state.approval.value;
    if (designApproved && !this.requestHasErrors()) {
      if (!result) {
        this.setState({ hasErrorReceiveOfferForm: true });
        this.dialogContentRef.current.scrollTo(0, 0);
      }
      if (result) {
        this.setState({ hasErrorReceiveOfferForm: false });
      }
    } else {
      this.verifyRequestErrors();
    }
  };

  handleBasketNoticeChange = (event) => {
    this.setState({ notice: event.target.value });
    this.props.setConfigurationComment(event.target.value);
  };

  onSelectableChanged = (priceData) => (key, newSelected) => {
    const nextData = priceData.map((currentElement) => {
      if (currentElement.key === key) {
        return {
          ...currentElement,
          selected: newSelected,
          price: newSelected ? '10,00 €' : '0,00 €',
        };
      }
      return currentElement;
    });
    this.setState({ priceData: nextData });
  };

  priceListClick = () => {
    this.priceListRef.current.scrollIntoView();
  };

  showPriceListButton = (inView) => {
    this.setState({ inView: inView });
  };

  renderPriceOverview() {
    const { notice, showPriceOverview, approval, inView, showErrors, bulkNameErrors } = this.state;
    const {
      amount,
      priceList,
      priceFormatted,
      onSelectableChanged,
      showProductVariants,
      ProductVariantComponent,
      error,
      width,
      hidePrices,
      title,
      classes,
      showReceiveOfferForm,
      vatNotice,
      clientTexts,
      notifications,
      showCallToAction,
    } = this.props;

    const { termsAndConditionsLink } = clientTexts;
    const mergedNotifications = [...notifications] || [];

    if (showErrors && bulkNameErrors && bulkNameErrors.length > 0) {
      bulkNameErrors.map((error) => {
        mergedNotifications.push({
          message: error,
          level: 'error',
          color: 'error',
        });
      });
    }

    return (
      <Dialog
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        classes={{
          paper: classes.dialogWrapper,
          container: width === 'md' || width === 'xs' ? classes.dialogPositioning : '',
          root: classes.dialogPositioning,
        }}
        BackdropProps={{ className: classes.dialogPositioning }}
        onClose={this.handleClose.bind(this)}
        open={showPriceOverview}
        maxWidth={'md'}
        fullWidth
        fullScreen={width === 'sm' || width === 'xs'}
        scroll="paper"
      >
        <LoadingOverlay show={this.props.addToCartPopUpLoader} />
        <DialogTitle classes={{ root: classes.dialogTitleRoot }}>
          <Typography variant="body1">
            <b>{t(showReceiveOfferForm ? 'receiveOfferForm.sendButton' : 'amountPrice.intoCart')}</b>
          </Typography>
          <Icon className={classes.closeIcon} onClick={this.handleClose.bind(this)}>
            close
          </Icon>
        </DialogTitle>
        <RootRef rootRef={this.dialogContentRef}>
          <DialogContent classes={{ root: classes.contentPositioning }}>
            {this.state.hasErrorReceiveOfferForm && (
              <Typography color="error" variant="body2">
                {t('receiveOfferForm.checkInputsInForm')}
              </Typography>
            )}
            <PriceOverview
              title={t('amountPrice.productVariants')}
              contactData={t('amountPrice.contactData')}
              handleClose={this.handleClose}
              amount={amount}
              acceptDesignComponent={
                showCallToAction ? (
                  <AcceptDesign
                    termsAndConditionsLink={termsAndConditionsLink}
                    displayAcceptDesign={true}
                    handleChangeCheckboxApproval={this.handleChange}
                    approval={approval}
                    closeProductOverviewDialog={this.handleClose}
                    notice={notice}
                    noteLabel={t('amountPrice.notice')}
                    handleChange={this.handleBasketNoticeChange}
                  />
                ) : null
              }
              notificationComponent={mergedNotifications.map(({ message, level, color }, key) => {
                return <Notification key={key} icon={level} color={color} notification={message} />;
              })}
              priceListComponent={
                <div ref={this.priceListRef}>
                  <InView as="div" onChange={(inView) => this.showPriceListButton(inView)}>
                    <PriceList
                      amount={amount}
                      list={priceList}
                      totalPrice={priceFormatted}
                      taxNote={t('amountPrice.vatNotice')}
                      onSelectableChanged={onSelectableChanged}
                      hidePrices={hidePrices}
                      showReceiveOfferForm={showReceiveOfferForm}
                      productTitle={title}
                    />
                  </InView>
                </div>
              }
              productVariantComponent={showProductVariants && <ProductVariantComponent />}
              receiveOfferFormComponent={<ReceiveOfferForm innerRef={this.receiveOfferFormComponentRef} />}
              showReceiveOfferForm={showReceiveOfferForm}
            />
          </DialogContent>
        </RootRef>
        <DialogActionsWrapper onTouchMove={this.handleTouchMove}>
          <AddToCart
            buttonLabel={t(showReceiveOfferForm ? 'receiveOfferForm.sendButton' : 'amountPrice.intoCart')}
            showCallToAction={showCallToAction}
            onPress={showReceiveOfferForm ? this.onReceiveOfferFormPress : this.onAddToBasket}
            handleChange={this.handleBasketNoticeChange}
            disabled={!!error || !approval.value}
            totalPrice={priceFormatted}
            taxNote={vatNotice}
            showPriceList={!inView}
            priceListLabel={t('amountPrice.showPriceList')}
            priceListClick={this.priceListClick}
            hidePrices={hidePrices}
            amount={amount}
            showReceiveOfferForm={showReceiveOfferForm}
          />
        </DialogActionsWrapper>
      </Dialog>
    );
  }

  openSummary = async () => {
    try {
      this.props.fetchSummary();

      this.setState({ showSummary: true });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`Error while validating configuration: ${e}`);
    }
  };

  renderSummary = () => {
    const { classes, summary } = this.props;
    return (
      <CustomDialog
        open={true}
        dialogTitle={t('amountPrice.summary')}
        onCancel={() => this.setState({ showSummary: false })}
        className={classes.summaryDialog}
        fullWidth
      >
        <div className={classes.summaryDialogContent}>
          {summary.length === 0 && <Progress loading={true} />}
          {summary.map((category, i) => (
            <div key={category.identifier + i}>
              <Typography className={classes.categoryTitle} variant="body1">
                {category.title}
              </Typography>
              {category.options.map((option, i) => (
                <div className={classes.selectedOptionWithPrice} key={i}>
                  <Typography className={classes.optionDescriptionText} variant="body2">{option.title}</Typography>
                  <Typography variant="body2">{option.priceFormatted}</Typography>
                </div>
              ))}
            </div>
          ))}
        </div>
      </CustomDialog>
    );
  };

  render() {
    const {
      classes,
      boxed,
      priceFormatted,
      hidePrices,
      hidePieces,
      amount,
      vatNotice,
      showReceiveOfferForm,
      showCallToAction,
      wrapperClass,
      showConfigurationModeSwitch,
      ConfigurationModeSwitchComponent,
    } = this.props;

    const { showPriceOverview, showSummary } = this.state;

    const boxStyle = boxed ? classes.card : classes.cardUnboxed;

    return (
      <React.Fragment>
        <Card className={clsx(boxStyle, wrapperClass, customClassName('cta-box'))}>
          <Grid item xs={3} sm={12}>
            <CardContent
              className={classes.cardContentWrapper}
              classes={{ root: classes.AmountPriceContent }}
            >
              <Grid container>
                {!hidePieces && (
                  <Grid item xs={10} sm={4} md={3} lg={3}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={this.openPriceOverview}
                      classes={{
                        root: classes.AmountPriceButton,
                        sizeSmall: classes.smallButtonSize,
                      }}
                    >
                      {amount} {t('amountPrice.pieces')}
                    </Button>
                  </Grid>
                )}
                <Grid item xs={10} sm={4} md={3} lg={3} className={classes.summary}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={this.openSummary}
                    classes={{
                      root: classes.summaryButton,
                      sizeSmall: classes.smallButtonSize,
                    }}
                  >
                    {t('amountPrice.summary')}
                  </Button>
                </Grid>
                {!hidePrices && (
                  <Grid item xs={10} sm={4} md={6} lg={6}>
                    <Typography
                      variant="h5"
                      classes={{
                        root: classes.AmountPriceTypography,
                      }}
                    >
                      <b>{priceFormatted}</b>
                    </Typography>
                    {priceFormatted && (
                      <Typography
                        className={classes.pos}
                        variant="caption"
                        classes={{
                          root: classes.AmountPriceTypographyVat,
                        }}
                      >
                        <i>{vatNotice}</i>
                      </Typography>
                    )}
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Grid>
          <Grid item xs={9} sm={12}>
            <CardActions className={classes.cardActions}>
              <Grid container spacing={1}>
                {showConfigurationModeSwitch && (
                  <Grid item xs={showCallToAction ? 6 : 12}>
                    <ConfigurationModeSwitchComponent
                      className={customClassName('configuration-type-button')}
                      classes={{
                        root: classes.mainButton,
                        label: classes.mainButtonLabel,
                      }}
                    />
                  </Grid>
                )}
                {showCallToAction && (
                  <Grid item xs={showConfigurationModeSwitch ? 6 : 12}>
                    <AnalyticsWrapper targetId="add_to_cart">
                      <MainButton
                        className={customClassName('cta-button')}
                        variant="contained"
                        fullWidth
                        size="medium"
                        onClick={this.openPriceOverview}
                        classes={{
                          root: classes.mainButton,
                          label: classes.mainButtonLabel,
                        }}
                      >
                        {t(showReceiveOfferForm ? 'receiveOfferForm.sendButton' : 'amountPrice.intoCart')}
                      </MainButton>
                    </AnalyticsWrapper>
                  </Grid>
                )}
              </Grid>
            </CardActions>
          </Grid>
        </Card>
        {showPriceOverview && this.renderPriceOverview()}
        {showSummary && this.renderSummary()}
      </React.Fragment>
    );
  }
}

export default withWidth()(withStyles(styles, { name: 'AmountPrice' })(AmountPrice));
